import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../environments/environment';
import { UIRoutes } from '../UIRoutes';

@Injectable({
    providedIn: 'root'
})
export class UtilService {

    getBrowserDetails(): any {
        const userAgent = navigator.userAgent;
        const language = navigator.language || (navigator as any).userLanguage;
        const screenHeight = screen.height;
        const screenWidth = screen.width;
        const colorDepth = screen.colorDepth;
        const timezoneOffset = new Date().getTimezoneOffset();
        const jsEnabled = (typeof window !== "undefined" && typeof document !== "undefined") ? "true" : "false";

        const browserDetails = {
            user_agent: userAgent,
            browser_tz: timezoneOffset.toString(),
            browser_color_depth: colorDepth.toString(),
            browser_screen_height: screenHeight.toString(),
            browser_screen_width: screenWidth.toString(),
            browser_language: language,
            browser_javascript_enabled: jsEnabled
        };

        return browserDetails;
    }

    billDeskFlowConfig(response) {
        return {
            merchantId: response.mercid,
            bdOrderId: response.bdOrderId,
            authToken: response.rdata,
            childWindow: false,
            returnUrl: this.getMobileSafeBillDeskReturnUrl(response),
            retryCount: 0,
            /* prefs: {
              "payment_categories": [ "upi", "qr", "gpay" ]
            } */
            prefs: {
                "payment_categories": ["upi", "qr", "gpay", "nb", "card"]
            }
        }
    }

    launchBillDeskSdk(config: any): void {
        this.enableBillDeskMobileFullScreen();
        this.listenForBillDeskReturn(config.flowConfig.returnUrl);
        const responseHandler = config.responseHandler;
        const sdkConfig = {
            ...config,
            responseHandler: (txn) => {
                try {
                    if (typeof responseHandler === 'function') {
                        responseHandler(txn);
                    }
                } finally {
                    this.clearBillDeskUiState();
                }
            }
        };

        (window as any).loadBillDeskSdk(sdkConfig);
    }

    private enableBillDeskMobileFullScreen(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        document.body.classList.add('billdesk-sdk-active');

        const applyFullScreenClass = () => {
            const elements = document.querySelectorAll(
                '[id*="billdesk" i], [class*="billdesk" i], [id^="bd" i], [class^="bd" i], iframe'
            );
            elements.forEach(element => element.classList.add('billdesk-mobile-fullscreen'));
        };

        applyFullScreenClass();

        const observer = new MutationObserver(applyFullScreenClass);
        observer.observe(document.body, { childList: true, subtree: true });

        setTimeout(() => observer.disconnect(), 10000);
    }

    clearBillDeskUiState(): void {
        if (typeof document === 'undefined') {
            return;
        }

        document.body.classList.remove('billdesk-sdk-active');
        document.body.classList.remove('billdesk-payment-active');
        document.querySelectorAll('.billdesk-mobile-fullscreen')
            .forEach(element => element.classList.remove('billdesk-mobile-fullscreen'));
    }

    clearBootstrapModalState(): void {
        if (typeof document === 'undefined') {
            return;
        }

        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
        document.querySelectorAll('.modal-backdrop')
            .forEach(element => element.remove());
    }

    // BillDesk POSTs the payment result to this URL, so it must always be the backend endpoint (ru).
    // Never point it at an Angular route: the SPA is hosted on S3, which rejects the POST with
    // "405 MethodNotAllowed", and the backend never records the payment.
    private getMobileSafeBillDeskReturnUrl(response: any): string {
        const returnUrl = response?.ru;
        if (!returnUrl) {
            return returnUrl;
        }

        try {
            const parsedReturnUrl = new URL(returnUrl);
            if (environment.apiBaseUrl &&
                (parsedReturnUrl.hostname === 'localhost' || parsedReturnUrl.hostname === '127.0.0.1')) {
                const apiBaseUrl = new URL(environment.apiBaseUrl);

                parsedReturnUrl.protocol = apiBaseUrl.protocol;
                parsedReturnUrl.host = apiBaseUrl.host;
                parsedReturnUrl.port = apiBaseUrl.port;
            }

            if (Capacitor.isNativePlatform()) {
                // The app runs at https://localhost (Android) / capacitor://localhost (iOS), not on
                // bookingdexter.com. Tell the backend where to send the user back so they stay in the
                // app - Capacitor hands any other host to the system browser.
                const platform = Capacitor.getPlatform();
                const appOrigin = platform === 'ios' ? 'capacitor://localhost' : window.location.origin;
                parsedReturnUrl.searchParams.set('returnOrigin', appOrigin);
                if (platform === 'android' || platform === 'ios') {
                    parsedReturnUrl.searchParams.set('returnPlatform', platform);
                }
            }
            return parsedReturnUrl.toString();
        } catch (error) {
            console.log('Unable to parse BillDesk return URL', error);
            return returnUrl;
        }
    }

    // When the backend's return page loads inside the SDK iframe it posts 'billdesk-return' to this
    // window instead of navigating (it cannot navigate the app's top window from the iframe). Load
    // the status page with a relative URL so it stays on the app's own origin.
    private billDeskReturnListener?: (event: MessageEvent) => void;
    private listenForBillDeskReturn(returnUrl: string): void {
        if (this.billDeskReturnListener) {
            window.removeEventListener('message', this.billDeskReturnListener);
        }
        const returnOrigin = new URL(returnUrl, window.location.origin).origin;
        const handler = (event: MessageEvent) => {
            const data = event.data;
            if (event.origin !== returnOrigin || !data || data.type !== 'billdesk-return' ||
                typeof data.orderId !== 'string' || !data.orderId.trim()) {
                return;
            }
            window.removeEventListener('message', handler);
            this.billDeskReturnListener = undefined;
            this.clearBillDeskUiState();
            // Payment-link orders reach BillDesk as <confirmationId>_<n>; the status page wants the base id.
            const orderId = String(data.orderId || '').split('_')[0];
            window.location.assign(orderId
                ? UIRoutes.ROUTING_MODULE_TRANSACTION_PAYMENT_STATUS + encodeURIComponent(orderId)
                : '/');
        };
        this.billDeskReturnListener = handler;
        window.addEventListener('message', handler);
    }
}
